import React, { useMemo } from 'react';
import {
  Grid, Typography, Card, CardContent, Chip, Box, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { RegiaoComprasDrillDownContentProps } from '../../types/regiaoComprasDrillDown';
import { useDataFormatters } from '../../Hooks/useDataFormatters';
import { formatYAxisValue } from '../DrillDown/formatters';

const RegiaoComprasDrillDownContent: React.FC<RegiaoComprasDrillDownContentProps> = ({ data }) => {
  const { formatCurrency, formatNumber } = useDataFormatters();
  
  const formatPercent = (value: number) => `${value.toFixed(2)}%`;

  const evolucaoData = useMemo(() => {
    if (!data.evolucao_mensal || !Array.isArray(data.evolucao_mensal)) {
      return [{
        id: 'Compras',
        data: []
      }];
    }
    
    // Filtrar e validar os dados antes de mapear
    const validData = data.evolucao_mensal.filter(item => 
      item && 
      typeof item === 'object' && 
      item.periodo !== undefined && 
      item.compras !== undefined &&
      item.periodo !== null &&
      item.compras !== null
    );
    
    const mappedData = validData.map(item => ({ 
      x: String(item.periodo), 
      y: Number(item.compras) || 0 
    }));
    
    return [{
      id: 'Compras',
      data: mappedData
    }];
  }, [data.evolucao_mensal]);

  const municipiosData = useMemo(() => {
    if (!data.top_municipios || !Array.isArray(data.top_municipios)) {
      return [];
    }
    // Ordenar municípios por total de compras em ordem crescente (maior no topo do gráfico horizontal)
    return data.top_municipios
      .map(m => ({ municipio: m.municipio, 'Total Compras': m.total_compras }))
      .sort((a, b) => a['Total Compras'] - b['Total Compras']);
  }, [data.top_municipios]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Resumo da Região: {data.regiao?.uf || 'N/A'}
            </Typography>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={4} md={3}><Chip label={`Total Compras: ${formatCurrency(data.regiao?.total_compras || 0)}`} color="success" sx={{ width: '100%' }} /></Grid>
              <Grid item xs={6} sm={4} md={3}><Chip label={`Participação: ${formatPercent(data.regiao?.participacao_percentual || 0)}`} color="primary" sx={{ width: '100%' }} /></Grid>
              <Grid item xs={6} sm={4} md={3}><Chip label={`Ticket Médio: ${formatCurrency(data.regiao?.ticket_medio || 0)}`} color="info" sx={{ width: '100%' }} /></Grid>
              <Grid item xs={6} sm={4} md={3}><Chip label={`Forncedores: ${formatNumber(data.regiao?.fornecedores_distintos || 0)}`} color="warning" sx={{ width: '100%' }} /></Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Evolução das Compras</Typography>
            <Box sx={{ height: 300 }}>
              {evolucaoData[0]?.data && evolucaoData[0].data.length > 0 ? (
                <ResponsiveLine
                  data={evolucaoData}
                  margin={{ top: 20, right: 110, bottom: 50, left: 80 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', stacked: false }}
                  axisLeft={{ format: formatYAxisValue }}
                  colors={{ scheme: 'category10' }}
                  pointSize={10}
                  useMesh={true}
                />
              ) : (
                <Box 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="body2">
                    Nenhum dado de evolução disponível para esta região
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Top 10 Municípios</Typography>
            <Box sx={{ height: 400 }}>
                <ResponsiveBar
                    data={municipiosData}
                    keys={['Total Compras']}
                    indexBy="municipio"
                    layout="horizontal"
                    margin={{ top: 20, right: 130, bottom: 50, left: 120 }}
                    padding={0.3}
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    colors={{ scheme: 'nivo' }}
                    borderColor={{ from: 'color', modifiers: [ [ 'darker', 1.6 ] ] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Total Compras',
                        legendPosition: 'middle',
                        legendOffset: 32,
                        format: formatYAxisValue
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor={{ from: 'color', modifiers: [ [ 'darker', 1.6 ] ] }}
                    animate={true}
                />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Top 10 Fornecedores na Região</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fornecedor</TableCell>
                    <TableCell align="right">Compras</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.top_fornecedores && Array.isArray(data.top_fornecedores) ? (
                    data.top_fornecedores.map((fornecedor, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{fornecedor?.nome || 'N/A'}</TableCell>
                        <TableCell align="right">{formatCurrency(fornecedor?.valor_compras || 0)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nenhum fornecedor encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RegiaoComprasDrillDownContent; 